import { z } from 'zod';

/**
 * Contracts for the admin feedback endpoints (admin/feedback…). Unknown enum values
 * are kept as strings so a new category or source shows up instead of breaking the list.
 *
 * A website visitor has isGuest: true, with id, userId and phoneNumber null.
 * @typedef {{ isGuest: boolean, id: number|string|null, userId: string|null, name: string|null, email: string|null, phoneNumber: string|null }} Customer
 * @typedef {{ id: number|string, url: string, fileName: string|null, mimeType: string|null, sizeBytes: number|null, createdAt: string|null }} Attachment
 * @typedef {{ id: number|string, toEmail: string|null, fromEmail: string|null, subject: string, body: string,
 *   emailStatus: string, errorCode: string|null, admin: { id: number|string, name: string|null }|null,
 *   attachments: Attachment[], createdAt: string|null }} Reply
 * @typedef {{ id: number, ticketNo: string, subject: string, message: string, category: string, status: string,
 *   source: string|null, replyCount: number, attachmentCount: number, lastRepliedAt: string|null, createdAt: string|null,
 *   customer: Customer }} FeedbackRow
 * @typedef {FeedbackRow & { appVersion: string|null, deviceInfo: string|null, lang: string|null, placeId: string|null,
 *   updatedAt: string|null, attachments: Attachment[], replies: Reply[],
 *   replyDefaults: { to: string|null, from: string|null, subject: string } }} FeedbackDetail
 * @typedef {{ all: number, byStatus: Record<string, number>, byCategory: Record<string, number> }} FeedbackCounts
 * @typedef {{ page: number, limit: number, totalItems: number, totalPages: number, hasNextPage: boolean, hasPrevPage: boolean }} Pagination
 */

const nullableStr = z.string().nullable().optional().transform((v) => v ?? null);
const str = z.string().nullable().optional().transform((v) => v ?? '');
const count = z.number().int().nonnegative().nullable().optional().transform((v) => v ?? 0);
const id = z.union([z.number(), z.string()]);

const customerSchema = z
  .object({
    isGuest: z.boolean().nullable().optional().transform((v) => v === true),
    id: id.nullable().optional(),
    userId: nullableStr,
    name: nullableStr,
    email: nullableStr,
    phoneNumber: z.union([z.string(), z.number()]).nullable().optional().transform((v) => (v == null ? null : String(v))),
  })
  .nullable()
  .optional()
  .transform((c) => c ?? { isGuest: false, id: null, userId: null, name: null, email: null, phoneNumber: null });

export const attachmentSchema = z.object({
  id,
  url: z.string(),
  fileName: nullableStr,
  mimeType: nullableStr,
  sizeBytes: z.number().nonnegative().nullable().optional().transform((v) => v ?? null),
  createdAt: nullableStr,
});

const attachments = z
  .array(z.unknown())
  .nullable()
  .optional()
  .transform((list) => (list ?? []).flatMap((raw) => {
    const parsed = attachmentSchema.safeParse(raw);
    return parsed.success ? [parsed.data] : [];
  }));

export const replySchema = z.object({
  id,
  toEmail: nullableStr,
  fromEmail: nullableStr,
  subject: str,
  body: str,
  emailStatus: z.string(),
  errorCode: nullableStr,
  admin: z
    .object({ id: id.nullable().optional(), name: nullableStr })
    .nullable()
    .optional()
    .transform((a) => a ?? null),
  attachments,
  createdAt: nullableStr,
});

export const feedbackRowSchema = z.object({
  id: z.number().int().positive(),
  ticketNo: z.string(),
  subject: str,
  message: str,
  category: z.string(),
  status: z.string(),
  source: nullableStr,
  replyCount: count,
  attachmentCount: count,
  lastRepliedAt: nullableStr,
  createdAt: nullableStr,
  customer: customerSchema,
});

export const feedbackDetailSchema = feedbackRowSchema.extend({
  appVersion: nullableStr,
  deviceInfo: nullableStr,
  lang: nullableStr,
  placeId: z.union([z.string(), z.number()]).nullable().optional().transform((v) => (v == null ? null : String(v))),
  updatedAt: nullableStr,
  attachments,
  replies: z
    .array(z.unknown())
    .nullable()
    .optional()
    .transform((list) => (list ?? []).flatMap((raw) => {
      const parsed = replySchema.safeParse(raw);
      return parsed.success ? [parsed.data] : [];
    })),
  replyDefaults: z
    .object({ to: nullableStr, from: nullableStr, subject: str })
    .nullable()
    .optional()
    .transform((d) => d ?? { to: null, from: null, subject: '' }),
});

const countMap = z.record(z.string(), z.number()).nullable().optional().transform((m) => m ?? {});

export const feedbackListSchema = z.object({
  feedback: z.array(z.unknown()),
  counts: z
    .object({ all: count, byStatus: countMap, byCategory: countMap })
    .nullable()
    .optional()
    .transform((c) => c ?? { all: 0, byStatus: {}, byCategory: {} }),
  pagination: z.object({
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
    hasNextPage: z.boolean(),
    hasPrevPage: z.boolean(),
  }),
});
